import { apiClient } from './client';
import type {
  ApprovalRequest,
  ApprovalRequestDetail,
  ApprovalStats,
  ApproveAction,
  RejectAction,
  ApprovalResult,
} from '@/types';

/**
 * API модуль для HITL (Human-in-the-Loop) операций
 * @contract HITL Approval API
 */

/**
 * Получить список ожидающих подтверждения действий
 */
export async function getPendingApprovals(): Promise<ApprovalRequest[]> {
  const response = await apiClient.get<ApprovalRequest[]>('/hitl/pending');
  return response.data;
}

/**
 * Получить детали запроса на подтверждение
 * @param approvalId ID запроса
 */
export async function getApprovalDetails(approvalId: string): Promise<ApprovalRequestDetail> {
  const response = await apiClient.get<ApprovalRequestDetail>(`/hitl/${approvalId}`);
  return response.data;
}

/**
 * Подтвердить действие
 * @param approvalId ID запроса
 * @param data Данные подтверждения
 */
export async function approveAction(
  approvalId: string,
  data?: ApproveAction,
): Promise<ApprovalResult> {
  const response = await apiClient.post<ApprovalResult>(`/hitl/${approvalId}/approve`, data || {});
  return response.data;
}

/**
 * Отклонить действие
 * @param approvalId ID запроса
 * @param data Данные отклонения
 */
export async function rejectAction(
  approvalId: string,
  data?: RejectAction,
): Promise<ApprovalResult> {
  const response = await apiClient.post<ApprovalResult>(`/hitl/${approvalId}/reject`, data || {});
  return response.data;
}

/**
 * Получить статистику по approvals
 * @param startDate Начало периода (ISO)
 * @param endDate Конец периода (ISO)
 */
export async function getApprovalStats(
  startDate?: string,
  endDate?: string,
): Promise<ApprovalStats> {
  const response = await apiClient.get<ApprovalStats>('/hitl/stats', {
    params: { startDate, endDate },
  });
  return response.data;
}

/**
 * UX-HITL-1 part 2: drill-down по категории.
 * Возвращает список approvals (включая already-decided) для данной категории.
 *
 * @param category Категория (financial / external_api / memory_deletion / other)
 * @param options paging
 */
export async function getApprovalsByCategory(
  category: string,
  options: { limit?: number; offset?: number } = {},
): Promise<ApprovalRequest[]> {
  const response = await apiClient.get<ApprovalRequest[]>(
    `/hitl/by-category/${encodeURIComponent(category)}`,
    {
      params: {
        limit: options.limit,
        offset: options.offset,
      },
    },
  );
  return response.data;
}
